import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionsActionTypes from "../extractions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { parseBackendErrors } from "../../commonUtils/queryBuilder.helper.js";
import { setQBErrors } from "../extractions/action";


function* getExtCustomColumnQueriesSaga({ params }) {
    // const { currentPage, perPage } = params;

    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const filters = yield select((state) => state.ExtCustomColumnQueries.filters);
        const { extractionData } = yield select((state) => state.Extractions);
        const { extCustomColumn } = yield select((state) => state.ExtCustomColumns);

        let queryString = '';
        // let queryString = `currentPage=${currentPage}&perPage=${perPage}`;
        if(filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);

        const { data } = yield API.get(`/ext/${extractionData.id}/custom-columns/${extCustomColumn.id}/queries?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getExtCustomColumnQuerySaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { extractionData } = yield select((state) => state.Extractions);
        const { extCustomColumn } = yield select((state) => state.ExtCustomColumns);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: { message: "Extraction ID not found" }
            });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/custom-columns/${extCustomColumn.id}/queries/${id}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERY_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load custom column query.");
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERY_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load custom column query.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERY_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* createExtCustomColumnQuerySaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { formData } = payload;
        const { extractionData } = yield select((state) => state.Extractions);
        const { extCustomColumn } = yield select((state) => state.ExtCustomColumns);
        
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        if (!extCustomColumn?.id) {
            toast.error("Custom Column ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: { message: "Custom Column ID not found" }
            });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/custom-columns/${extCustomColumn.id}/queries`, formData);
        
        if (data.success) {
            toast.success("Custom Column created successfully!");
            
            // Clear any previous errors on success
            yield put(setQBErrors({}));
            
            yield put({ 
                type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY_SUCCESS, 
                payload: data.data 
            });
            
            // Refresh the list
            // const { pagination } = yield select((state) => state.ExtCustomColumnQueries.ExtCustomColumnQueriesList);
            // const params = { 
            //     currentPage: pagination?.currentPage || 1, 
            //     perPage: pagination?.perPage || 10 
            // };
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES });
            // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES, params });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save custom column.");
            }
            yield put({ 
                type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        
        // Check if we have backend validation errors
        if (errorData?.data) {
            // Parse backend validation errors into qbErrors format
            const parsedErrors = parseBackendErrors(errorData.data);
            
            // Dispatch parsed errors to qbErrors in Extractions state
            yield put(setQBErrors(parsedErrors));
            
            toast.error("Please fix validation errors");
        } else {
            // Generic error handling for non-validation errors
            handleApiError(error, "Could not save custom column.");
        }
        
        yield put({ 
            type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            }
        });
    }
};

function* updateExtCustomColumnQuerySaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { id, formData } = payload;
        const { extractionData } = yield select((state) => state.Extractions);
        const { extCustomColumn } = yield select((state) => state.ExtCustomColumns);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(`/ext/${extractionData.id}/custom-columns/${extCustomColumn.id}/queries/${id}`, formData);
        
        if (data.success) {
            toast.success("Custom Column updated successfully!");
            
            // Clear any previous errors on success
            yield put(setQBErrors({}));
            
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERY_SUCCESS, 
                payload: data.data 
            });
            
            // Refresh the list
            // const { pagination } = yield select((state) => state.ExtCustomColumnQueries.ExtCustomColumnQueriesList);
            // const params = { 
            //     currentPage: pagination?.currentPage || 1, 
            //     perPage: pagination?.perPage || 10 
            // };
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES });
            // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES, params });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save updated custom column data.");
            }
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        
        // Check if we have backend validation errors
        if (errorData?.data) {
            // Parse backend validation errors into qbErrors format
            const parsedErrors = parseBackendErrors(errorData.data);
            
            // Dispatch parsed errors to qbErrors in Extractions state
            yield put(setQBErrors(parsedErrors));
            
            toast.error("Please fix validation errors");
        } else {
            // Generic error handling for non-validation errors
            handleApiError(error, "Could not save updated custom column data.");
        }
        
        yield put({ 
            type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtCustomColumnQueriesOrderSaga({ payload }) {
    try {
        const { orderData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERIES_ORDER_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/custom-column/update-order`, 
            { orderData }
        );

        if (data.success) {
            // Success toast removed for better UX
            // toast.success("Order updated successfully!");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERIES_ORDER_SUCCESS, 
                payload: data.data 
            });
            
            // Optional: Refresh the list only on page change or filter change
            // Not needed here since the context already updates the local state
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERIES_ORDER_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Could not update order.");
        const data = error?.response?.data;
        
        // On error, we might want to refresh the list to revert to correct order
        toast.error("Failed to update order. Refreshing...");
        // const { pagination } = yield select((state) => state.ExtCustomColumnQueries.ExtCustomColumnQueriesList);
        // const params = { 
        //     currentPage: pagination?.currentPage || 1, 
        //     perPage: pagination?.perPage || 10 
        // };
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES });
        // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES, params });
        
        yield put({ 
            type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERIES_ORDER_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
};

function* deleteExtCustomColumnQuerySaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { extractionData } = yield select((state) => state.Extractions);
        const { extCustomColumn } = yield select((state) => state.ExtCustomColumns);
        const { data } = yield API.delete(`/ext/${extractionData.id}/custom-columns/${extCustomColumn.id}/queries/${Id}`);

        if (data.success) {
            // const { pagination } = yield select((state) => state.ExtCustomColumnQueries.ExtCustomColumnQueriesList);
            // const params = { currentPage: pagination.currentPage, perPage: pagination.perPage };
            yield put({ type: actionTypes.DELETE_EXT_CUSTOM_COLUMN_QUERY_SUCCESS, payload: data });
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES });
            // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES, params });
        } else {
            toast.error(data?.message ||"Could not delete custom column.");
            yield put({ type: actionTypes.DELETE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete custom column.");
        const data = error?.response?.data;
        toast.error(data?.message ||"Could not delete custom column.");
        yield put({ type: actionTypes.DELETE_EXT_CUSTOM_COLUMN_QUERY_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* setOpenModalSaga({ payload }) {
    // If closing the modal (payload = false), reset the query builder
    if (!payload) {
        yield put({ type: extractionsActionTypes.RESET_QUERY_BUILDER });
    }
}

function* ExtCustomColumnQueriesSaga() {
  yield all([
    takeLatest(actionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES, getExtCustomColumnQueriesSaga),
    takeLatest(actionTypes.GET_EXT_CUSTOM_COLUMN_QUERY, getExtCustomColumnQuerySaga),
    takeLatest(actionTypes.CREATE_EXT_CUSTOM_COLUMN_QUERY, createExtCustomColumnQuerySaga),
    takeLatest(actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERY, updateExtCustomColumnQuerySaga),
    takeLatest(actionTypes.UPDATE_EXT_CUSTOM_COLUMN_QUERIES_ORDER, updateExtCustomColumnQueriesOrderSaga),
    takeLatest(actionTypes.DELETE_EXT_CUSTOM_COLUMN_QUERY, deleteExtCustomColumnQuerySaga),
    takeLatest(actionTypes.SET_OPEN_MODAL, setOpenModalSaga)
  ]);
}

export default ExtCustomColumnQueriesSaga;